import React, {
  createContext,
  useEffect,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from '@/utils/router';
import {
  createGlobalStyle,
  ThemeProvider as StyledThemeProvider,
} from 'styled-components';
import useMediaQuery from '@/hooks/useMediaQuery';
import { slugToTypeName, type EventType } from '@/constants/geo';
import { darkTheme, getThemeForEvent, Theme, type ThemeMode } from './themes';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';

const decodePathSegment = (segment = '') => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const eventTypeFromPathname = (pathname: string): EventType | null => {
  const segments = pathname.split('/').filter(Boolean).map(decodePathSegment);
  if (segments[0] !== 'תקליטן' || !segments[1]) return null;

  return slugToTypeName(segments[1]);
};

const defaultModeForEvent = (_eventType: EventType): ThemeMode => 'dark';

const GlobalScrollbarStyle = createGlobalStyle`
  html {
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.MenuItemSelectedColor} ${({ theme }) => theme.backgroundColor};
    background: ${({ theme }) => theme.backgroundColor};
  }

  #root {
    min-height: 100vh;
    background: ${({ theme }) => theme.backgroundColor};
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    html[dir='rtl'],
    html[dir='rtl'] body {
      direction: ltr;
    }

    html[dir='rtl'] #root {
      direction: rtl;
    }

    html[dir='ltr'] body,
    html[dir='ltr'] #root {
      direction: ltr;
    }
  }

  body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar {
    width: 12px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.backgroundColor};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      `linear-gradient(180deg, ${theme.gradientColors[0]}, ${theme.gradientColors[1]})`};
    border-radius: 999px;
    border: 3px solid ${({ theme }) => theme.backgroundColor};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.MenuItemSelectedColor};
  }
`;

export const ThemeContext = createContext<{
  theme: Theme;
  mode: ThemeMode;
  eventType: EventType;
  isDesktop: boolean;
  toggleTheme: () => void;
  setEventType: (eventType: EventType) => void;
}>({
  theme: darkTheme,
  mode: 'dark',
  eventType: 'אירוע',
  isDesktop: false,
  toggleTheme: () => {},
  setEventType: () => {},
});

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const routeEventType = useMemo(
    () => eventTypeFromPathname(location.pathname),
    [location.pathname]
  );
  const [mode, setMode] = useState<ThemeMode>(() =>
    defaultModeForEvent(routeEventType ?? 'אירוע')
  );
  const [eventType, setEventType] = useState<EventType>('אירוע');
  const hasManualModeSelection = useRef(false);
  const isDesktop = useMediaQuery(`(min-width: ${MEDIA_BREAKPOINTS.mediumScreen})`);
  const activeEventType = routeEventType ?? eventType;
  const theme = useMemo(() => getThemeForEvent(activeEventType, mode), [activeEventType, mode]);

  useEffect(() => {
    if (hasManualModeSelection.current) return;

    setMode(defaultModeForEvent(activeEventType));
  }, [activeEventType]);

  const toggleTheme = () => {
    hasManualModeSelection.current = true;
    setMode(prevMode => (prevMode === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, mode, eventType: activeEventType, isDesktop, toggleTheme, setEventType }}>
      <StyledThemeProvider theme={theme}>
        <GlobalScrollbarStyle />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
